import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import Page from "../../Components/Page";
import PageTitle from "../../Components/PageTitle";
import InsightCard from "../../Components/InsightCard";
import DatePresetSelector from "../../Components/DatePresetSelector";
import InstallPerformanceChart from "../../Components/InstallPerformanceChart";
import UnsuccessfulInstallsTable from "../../Components/UnsuccessfulInstallsTable";
import CircularProgress from "@mui/material/CircularProgress";
import PrimaryButton from "../../Components/PrimaryButton";
import { useGetUserRole } from "../../Services/Hooks";
import { useInstallPerformance } from "../../Services/Hooks/useInstallPerformance";
import { useAppSelector } from "State/hooks";
import {
  getPresetDateRange,
  flattenFailedRecords,
  PRESETS,
} from "./utils";
import type { PresetKey, Granularity } from "./utils";

const InstallPerformance = () => {
  const navigate = useNavigate();
  const { userIsManagerOrAdmin } = useGetUserRole();
  const user = useAppSelector((state) => state.user.authData);
  const orgId = user?.organisationId;

  // Role guard — redirect non-managers/admins
  useEffect(() => {
    if (!userIsManagerOrAdmin) {
      navigate("/");
    }
  }, [userIsManagerOrAdmin, navigate]);

  // Date controls state
  const defaultPreset = PRESETS[1]; // Last 4 weeks
  const [preset, setPreset] = useState<PresetKey>(defaultPreset.key);
  const [granularity, setGranularity] = useState<Granularity>(
    defaultPreset.defaultGranularity
  );
  const [customRange, setCustomRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);

  // Compute dates from preset
  const { startTime, endTime } = useMemo(
    () => getPresetDateRange(preset, customRange),
    [preset, customRange]
  );

  // Don't fetch if "custom" is selected but no dates have been picked yet
  const isCustomWithoutDates = preset === "custom" && !customRange;

  // Fetch data
  const { data, loading, error, refetch } = useInstallPerformance(
    isCustomWithoutDates ? undefined : orgId,
    granularity,
    startTime,
    endTime
  );

  // Derived values from current snapshot
  const kitsInstalled =
    data?.currentSnapshot?.kits?.inUseActiveServiceUsers ?? 0;
  const totalKits = data?.currentSnapshot?.kits?.count ?? 0;
  const kitsAvailable = useMemo(
    () =>
      data?.currentSnapshot?.kits?.records?.filter(
        (r) => r.serviceUserId === null
      ).length ?? 0,
    [data]
  );

  // Derived values from all-time summary
  const totalReferrals = data?.allTimeSummary?.referredServiceUsers ?? 0;
  const successfulInstalls = data?.allTimeSummary?.successfulInstalls ?? 0;

  // Re-use ratio
  const reuseRatio =
    totalKits > 0 ? (successfulInstalls / totalKits).toFixed(2) : "0.00";

  // Utilisation text
  const utilisationPct =
    totalKits > 0 ? Math.round((kitsInstalled / totalKits) * 100) : 0;
  const unusedKits = totalKits - kitsInstalled;

  // Failed records for table
  const failedRecords = useMemo(
    () => flattenFailedRecords(data?.periods ?? []),
    [data]
  );

  if (!userIsManagerOrAdmin) {
    return null;
  }

  return (
    <Page className={styles.page}>
      <div className={styles.content}>
        <PageTitle title="Install performance" />

        <DatePresetSelector
          preset={preset}
          setPreset={setPreset}
          granularity={granularity}
          setGranularity={setGranularity}
          customRange={customRange}
          setCustomRange={setCustomRange}
        />

        {loading && !data && (
          <div className={styles.loading}>
            <CircularProgress size={40} />
          </div>
        )}

        {error && !data && (
          <div className={styles.error}>
            <p>Failed to load install performance data.</p>
            <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
          </div>
        )}

        {error && data && (
          <div className={styles.stale_warning}>
            <p>Unable to refresh data. Showing cached results.</p>
            <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
          </div>
        )}

        {data && (
          <>
            {/* Current snapshot */}
            <section>
              <h2 className={styles.section_title}>Current snapshot</h2>
              <div className={styles.cards_grid}>
                <InsightCard
                  title="Kits currently installed"
                  primaryMetric={`${kitsInstalled}/${totalKits}`}
                  supportingText={`Against ${totalKits} contracted kits, leaving ${unusedKits} of the contracted allocation unused \u2014 ${utilisationPct}% utilisation rate.`}
                  progressBar={{
                    current: kitsInstalled,
                    total: totalKits,
                  }}
                  onClick={() =>
                    navigate("/lilli-users", { state: { filter: "complete" } })
                  }
                />
                <InsightCard
                  title="Kits available for deployment"
                  primaryMetric={kitsAvailable}
                  supportingText={`${kitsAvailable} kit${kitsAvailable !== 1 ? "s are" : " is"} not being used and ${kitsAvailable !== 1 ? "are" : "is"} available to install in new homes.`}
                />
              </div>
            </section>

            {/* All-time performance */}
            <section>
              <h2 className={styles.section_title}>All-time performance</h2>
              <div className={styles.cards_grid}>
                <InsightCard
                  title="Total referrals to date"
                  primaryMetric={totalReferrals}
                  supportingText="Residents referred to Lilli across the lifetime of the contract."
                />
                <InsightCard
                  title="Successful installs to date"
                  primaryMetric={successfulInstalls}
                  supportingText={`${successfulInstalls} successful installs against ${totalKits} available kits. So far each kit is being re-used ${reuseRatio} times.`}
                />
              </div>
            </section>

            {/* Chart */}
            <InstallPerformanceChart
              periods={data.periods}
              granularity={granularity}
            />

            {/* Unsuccessful installs table */}
            <UnsuccessfulInstallsTable
              records={failedRecords}
              range={data.metadata?.range}
              granularity={granularity}
            />
          </>
        )}
      </div>
    </Page>
  );
};

export default InstallPerformance;
